import React from 'react';
import classNames from 'classnames/bind';
import { useNavigate, useParams } from 'react-router-dom';
import type { CellProps, Column } from 'react-table';

import { ApplicationParams } from 'types/route_params';
import { CursorPaginated } from 'types/pagination';
import { Stream } from 'types/stream';

import { getLumeoFileIdFromStreamUri } from 'services/stream';
import { isNotNullOrUndefined } from 'services/nullable';
import { pluralize } from 'services/string';
import { relativeTimeFromNow } from 'services/date';
import { useAPI } from 'hooks/api/useAPI';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useDeleteFiles } from 'files/hooks/useDeleteFiles';
import { useDeleteStreams } from 'streams/hooks/useDeleteStreams';
import { useDownloadFile } from 'files/hooks/useDownloadFile';
import { useFileStreams } from 'files/hooks/useFileStreams';
import { useHasAccess } from 'hooks/useHasAccess';
import { useModal } from 'hooks/useModal';
import { useStreams, UseStreamsParams } from 'streams/hooks/useStreams';
import { useTagsFilterParams } from 'tags/hooks/useTagsFilterParams';
import { useUpdateStream } from 'streams/hooks/useUpdateStream';

import { Button } from 'components/Button/Button';
import { EmptyView } from 'components/EmptyView/EmptyView';
import { NameCell, TableLabelsCell } from 'components/Table/Table';
import { PaginatedTable } from 'components/Table/PaginatedTable';
import { QuerySearch } from 'components/QuerySearch/QuerySearch';
import { TableBulkActionType } from 'components/Table/components/BulkActions/BulkActions';
import { Text } from 'components/Text/Text';

import { SourceCell } from './SourceCell';
import styles from './Table.module.scss';

const c = classNames.bind(styles);

export function FilesOverviewTable() {
  const navigate = useNavigate();
  const [hasAccess] = useHasAccess();
  const { applicationID } = useParams<ApplicationParams>();

  const { client } = useAPI();

  const { open } = useModal();

  const tagFilters = useTagsFilterParams();
  const [queryFilters, setQueryFilters] = React.useState<
    UseStreamsParams & { page: number }
  >({
    page: 1,
  });

  const queryResult = useFileStreams(
    {
      ...tagFilters,
      ...queryFilters,
    },
    { keepPreviousData: true }
  );

  const [searchFilters, setSearchFilters] = React.useState<UseStreamsParams>(
    {}
  );
  const searchQueryResult = useStreams({
    params: {
      ...tagFilters,
      ...searchFilters,
      limit: 20,
      stream_types: ['file'],
    },
    enabled: Object.keys(searchFilters).length > 0,
  });

  const { data: currentPlan, isSuccess } = useCurrentPlan();
  const canCreateTags =
    (isSuccess && currentPlan === null) ||
    Boolean(currentPlan?.can_create_tags);

  const { mutate: updateStream } = useUpdateStream();
  const { mutateAsync: deleteStreams, isLoading: isDeleting } =
    useDeleteStreams();
  const { mutate: deleteFiles } = useDeleteFiles();
  const { download, isLoading: isDownloading } = useDownloadFile();

  const [isRenamingFile, setIsRenamingFile] = React.useState<Stream['id']>();

  const columns = React.useMemo(() => {
    const columns: Column<Stream>[] = [
      {
        Header: 'File',
        Cell(props: CellProps<Stream>) {
          function handleRename(name: string) {
            updateStream({ id: props.row.original.id, name });
            setIsRenamingFile(undefined);
          }

          function cancelRename() {
            setIsRenamingFile(undefined);
          }

          return (
            <NameCell
              {...props}
              isRenaming={isRenamingFile === props.row.original.id}
              onNameChange={handleRename}
              onCancel={cancelRename}
              entity="file"
            />
          );
        },
      },
      {
        id: 'source',
        Header: 'Source',
        Cell: SourceCell,
      },
    ];

    if (canCreateTags) {
      columns.push({
        Header: 'Labels',
        Cell(props: CellProps<Stream>) {
          return (
            <TableLabelsCell
              {...props}
              entity="streams"
              singleQueryKey={['stream']}
              listQueryKey={['streams']}
              readOnly={!hasAccess('deploy_edit', 'streams')}
            />
          );
        },
      });
    }

    columns.push({
      id: 'updated_at',
      Header: 'Uploaded',
      sortType: 'date',
      accessor(file: Stream) {
        return relativeTimeFromNow(file.updated_at);
      },
    });

    return columns;
  }, [canCreateTags, isRenamingFile, hasAccess, updateStream]);

  const handleSearchInput = React.useCallback((value?: string) => {
    if (!value) {
      setSearchFilters({});
      setQueryFilters({ page: 1 });
      return;
    }

    setSearchFilters({
      stream_names: [value],
    });
  }, []);

  function handleApplyFilters() {
    setQueryFilters({ ...searchFilters, page: 1 });
  }

  function handleSuggestionSelect({ id }: Stream) {
    navigate(id);
  }

  const handleBulkDelete = React.useCallback(
    async (ids: Array<string>) => {
      if (ids.length) {
        const { data } = await client.get<CursorPaginated<Stream>>(
          `/internal/apps/${applicationID}/streams`,
          {
            params: {
              pagination: 'cursor',
              stream_ids: ids,
              limit: ids.length,
            },
          }
        );

        // For streams created from user uploaded files
        // we need to delete the associated files
        const fileIds = data.data
          .map(getLumeoFileIdFromStreamUri)
          .filter(isNotNullOrUndefined);

        if (fileIds.length) {
          deleteFiles({ file_ids: fileIds });
        }

        return deleteStreams({ stream_ids: ids });
      }
    },
    [applicationID, client, deleteFiles, deleteStreams]
  );

  const hasFilters =
    Object.keys(queryFilters).length > 0 ||
    Object.values(tagFilters).some(Boolean);

  const bulkActions = React.useMemo(() => {
    function handleBulkDownload(fileIds: string[]) {
      fileIds.forEach((file) => download(file));
    }

    if (hasAccess('deploy_edit')) {
      return [
        {
          action: 'Download',
          loading: isDownloading,
          onClick: handleBulkDownload,
        },
        {
          action: 'Delete',
          icon: 'delete',
          intent: 'danger',
          loading: isDeleting,
          onClick: (ids) => {
            open('confirm-delete', {
              title: `Are you sure you want to delete ${pluralize(ids.length, 'this', String(ids.length))} ${pluralize(ids.length, 'file')}?`,
              onConfirm: () => handleBulkDelete(ids),
            });
          },
        },
      ] as TableBulkActionType[];
    }
  }, [download, handleBulkDelete, hasAccess, isDeleting, isDownloading, open]);

  return (
    <div className={c('wrap')}>
      <QuerySearch
        entityName="file"
        searchLabel="Search files..."
        searchResultField="name"
        searchQueryResult={searchQueryResult}
        onApplyFilters={handleApplyFilters}
        onSuggestionSelect={handleSuggestionSelect}
        onValueChange={handleSearchInput}
        totalElements={queryResult.data?.total_elements}
      />

      <PaginatedTable
        id="files-table"
        label="files"
        queryResult={queryResult}
        columns={columns}
        pageSize={50}
        page={queryFilters.page}
        onPageChange={(page) =>
          setQueryFilters((previous) => ({ ...previous, page }))
        }
        onRowNavigate={({ id }) => `/deploy/files/${id}`}
        emptyMessage={
          <div className={c('empty')}>
            {hasFilters ? (
              <EmptyView title="No files matching the filter criteria found.">
                Please try another filter combination or clear filters.
              </EmptyView>
            ) : (
              <EmptyView title="No files yet.">
                <Text type="paragraph">
                  Configure pipelines to upload files to the cloud or upload
                  files manually.
                </Text>
                <br />
                <Button to={`/applications/${applicationID}/deploy/files/new`}>
                  Upload files
                </Button>
              </EmptyView>
            )}
          </div>
        }
        bulkActions={bulkActions}
      />
    </div>
  );
}
